/* eslint-disable max-classes-per-file */
import {
    Component,
    Input,
    OnInit,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { SelectorComponent } from "@components/selector/selector.component";
import { TranslatePipe } from "@ngx-translate/core";
import { chain, flatten } from "lodash";
import { CommandService, ICommand } from "~app/common/services/cqs/command.service";
import { TransporttilldelaHandelssortimentCommand } from "~app/features/transport/transportplanera/models/commands";
import { ITplHandelssortiment } from "~app/features/transport/transportplanera/models/dtos";
import { IBefraktare, ITransportforetagForTilldelningQueryResult } from "~app/features/transport/transportplanera/models/queries";
import { TplArbetsorderViewModel } from "~app/features/transport/transportplanera/models/tplListViewModels";
import { TplService } from "~app/features/transport/transportplanera/services/tpl.service";
import { ValidationMessageRowComponent } from "../../../../../../../common/validation/validation-message-row/validation-message-row.component";
import { HandelssortimentDisplayMode, ITilldelningOption, TransporttilldelningOptions } from "../../models/tpl-transporttilldelning.model";

@Component({
    selector: "rask-tpl-transporttilldelning-hs",
    templateUrl: "./tpl-transporttilldelning-hs.component.html",
    styleUrls: ["./tpl-transporttilldelning-hs.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        SelectorComponent,
        FormsModule,
        ValidationMessageRowComponent,
        TranslatePipe
    ]
})
export class TplTransporttilldelningHsComponent implements OnInit {

    @Input() arbetsordrar: TplArbetsorderViewModel[];
    @Input() transportforetag: ITransportforetagForTilldelningQueryResult[];
    @Input() displayMode: HandelssortimentDisplayMode;

    options: TransporttilldelningOptions;
    applicableBefraktare: IBefraktare[];
    rows: TransporttilldelningHsRowViewmodel[];
    warningFdBefraktare: string;

    private readonly tplService = inject(TplService);
    private readonly commandService = inject(CommandService);

    ngOnInit(): void {
        const befraktare = this.getApplicableBefraktare();
        this.applicableBefraktare = befraktare;
        const transportforetag = this.getApplicableTransportforetag();
        const optionsContainer = new TransporttilldelningOptions(befraktare, transportforetag);

        this.options = optionsContainer;
        this.rows = this.buildRows();

        this.updateValidation();
    }

    optionFormatter = (item: ITilldelningOption) => item?.key;
    optionViewFormatter = (item: ITilldelningOption) => item?.value;

    emitCommand(command: ICommand): void {
        this.commandService.addTransactionCommand(command);
    }

    protected transportforetagChange($event): void {
        this.updateValidation();
    }

    private getApplicableBefraktare() {
        const hs = flatten(this.arbetsordrar.map(a => a.model.handelssortiment));
        const tilldelade = chain(hs)
            .filter(x => x.befraktareId > 0)
            .map(x => x.befraktareId)
            .uniq()
            .value();

        return this.tplService.allBefraktare()
            .filter(x => (x.aktiv && x.befraktare) || tilldelade.some(x1 => x1 === x.id));
    }

    private getApplicableTransportforetag() {
        const hs = flatten(this.arbetsordrar.map(a => a.model.handelssortiment));
        const tilldelade = chain(hs)
            .filter(x => x.transportforetagId > 0)
            .map(x => x.transportforetagId)
            .uniq()
            .value();
        if (this.arbetsordrar[0].model.transportforetagId > 0) {
            tilldelade.push(this.arbetsordrar[0].model.transportforetagId);
        }

        return this.transportforetag
            .filter(x => x.aktiv || tilldelade.some(x1 => x1 === x.id));
    }

    private buildRows(): TransporttilldelningHsRowViewmodel[] {
        const optionsContainer = this.options;
        const hsToOptionKey = new Map<number, string>();
        const uniqHs: ITplHandelssortiment[] = [];

        this.arbetsordrar.forEach(function(ao) {
            ao.model.handelssortiment.forEach(function(hs) {
                const optionKey = optionsContainer.getOptionKey(hs);

                if(hsToOptionKey.has(hs.handelssortimentId)) {
                    const existingOptionKey = hsToOptionKey.get(hs.handelssortimentId);
                    if(existingOptionKey !== optionKey) {
                        hsToOptionKey.set(hs.handelssortimentId, null);
                    }
                } else {
                    hsToOptionKey.set(hs.handelssortimentId, optionKey);
                    uniqHs.push(hs);
                }
            });
        });

        return uniqHs.map(hs => {
            const optionKey = hsToOptionKey.get(hs.handelssortimentId);
            const handelssortimentId = hs.handelssortimentId;
            const handelssortimentNamn = this.tplService.resolveHandelssortiment(hs.handelssortimentId).value;

            return new TransporttilldelningHsRowViewmodel(this, optionKey, handelssortimentId, handelssortimentNamn);
        });
    }

    private updateValidation(): void {
        const getKundIdFromSelected = (inp: string) => !!inp && inp.indexOf("/") > -1 ? parseInt(inp?.split("/")[1], 10) : -1;
        const fdBefraktare = this.applicableBefraktare.filter(x => !x.befraktare).map(x => x.id);
        const hasSelected = this.rows.some(x => fdBefraktare?.some(y => y === getKundIdFromSelected(x.selected)));

        this.warningFdBefraktare = hasSelected ? "Det finns kopplade transportföretag (kunder) som inte längre är märkta som befraktare." : null;
    }

}

export class TransporttilldelningHsRowViewmodel {
    private command: TransporttilldelaHandelssortimentCommand;

    constructor(
        private readonly parent: TplTransporttilldelningHsComponent,
        private optionKey: string,
        private readonly handelssortimentId: number,
        readonly handelssortimentNamn: string) {
    }

    get selected(): string { return this.optionKey; }

    set selected(key: string) {
        if (this.optionKey === key) {
            return;
        }

        this.optionKey = key;
        this.onModified();
    }

    get visible(): boolean {
        return this.parent.displayMode === HandelssortimentDisplayMode.all || !!this.optionKey;
    }

    private onModified(): void {
        if (!this.command) {
            this.command = new TransporttilldelaHandelssortimentCommand();

            this.command.arbetsorderIds = this.parent.arbetsordrar.map(a => a.model.id);
            this.command.handelssortimentId = this.handelssortimentId;

            this.parent.emitCommand(this.command);
        }

        const option = this.parent.options.get(this.optionKey);
        option?.bind(this.command);
    }
}
